'use client';

import type { FC, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { useUiMotionEnabled } from '../../hooks/useMotion';

interface CellProps extends HTMLAttributes<HTMLDivElement> {
  before?: ReactNode;
  after?: ReactNode;
  subtitle?: ReactNode;
  chevron?: boolean;
  isLast?: boolean;
  tone?: 'default' | 'destructive' | 'disabled' | 'accent';
  accentColor?: string;
}

export const Cell: FC<CellProps> = ({
  children,
  before,
  after,
  subtitle,
  chevron,
  isLast,
  tone = 'default',
  accentColor,
  className,
  onClick,
  ...props
}) => {
  const titleColor =
    tone === 'destructive' ? 'text-accent-red'
    : tone === 'disabled' ? 'text-label-tertiary'
    : tone === 'accent' ? ''
    : 'text-label-primary';

  const titleStyle = tone === 'accent' && accentColor ? { color: accentColor } : undefined;

  const afterIsChevronString = typeof after === 'string' && after === '›';
  const showChevron = chevron || afterIsChevronString;
  const trailingContent = afterIsChevronString ? undefined : after;

  const interactive = !!onClick && tone !== 'disabled';
  const motionEnabled = useUiMotionEnabled();

  const sepLeft = before !== undefined ? 'after:start-[42px]' : 'after:start-[14px]';

  return (
    <div
      onClick={interactive ? onClick : undefined}
      className={cn(
        'flex items-center w-full ps-[6px] pe-[8px] gap-[6px] relative',
        subtitle ? 'min-h-[58px]' : 'min-h-[44px]',
        !isLast && cn('after:content-[""] after:absolute after:end-[8px] after:bottom-0 after:h-px after:bg-white/[0.07]', sepLeft),
        isLast === undefined && 'last:after:hidden',
        interactive ? 'cursor-pointer' : 'cursor-default',
        interactive && 'active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.08)]',
        interactive && motionEnabled && 'shot-press-tint',
        className,
      )}
      {...props}
    >
      <div className="flex gap-[8px] items-center flex-1 min-w-0">
        {before !== undefined && (
          <span
            className={cn(
              'w-[28px] text-[15px] leading-[20px] text-center shrink-0 flex items-center justify-center',
              tone === 'disabled' && 'text-label-tertiary',
            )}
          >
            {before}
          </span>
        )}
        <div className="flex flex-col gap-[2px] items-start justify-center py-[11px] ps-[4px] flex-1 min-w-0">
          <span
            className={cn('text-[15px] leading-[18px] tracking-[0.2px] truncate w-full', titleColor)}
            style={titleStyle}
          >
            {children}
          </span>
          {subtitle !== undefined && (
            <span
              className={cn(
                'text-[13px] leading-[16px] truncate w-full',
                tone === 'disabled' ? 'text-label-tertiary' : 'text-label-secondary',
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {trailingContent !== undefined && (
        <span className="shrink-0 text-[15px] leading-[18px] tracking-[0.2px] text-end text-label-secondary truncate max-w-[60%]">
          {trailingContent}
        </span>
      )}
      {showChevron && (
        <span className="w-[14px] h-[20px] shrink-0 flex items-center justify-center text-label-tertiary rtl:-scale-x-100">
          <Chevron />
        </span>
      )}
    </div>
  );
};

const Chevron: FC = () => (
  <svg aria-hidden viewBox="0 0 7 12" width="7" height="12" className="shrink-0">
    <path
      d="M1 1 L6 6 L1 11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
